import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IktatgatoService } from '../iktatgato.service';
import { DataService } from '../../data.service';

/**
 *  Egy irat teljes képe: adatok, csatolt állományok, kapcsolódó iratok, postázás, napló.
 *
 *  Az irat életútja itt látszik és itt is lép: iktatott → kiadmányozott → expediált →
 *  elintézett. A kiadmányozás (vezetői jóváhagyás) és az expediálás (elküldés)
 *  szándékosan két lépés, mert a gyakorlatban más ember végzi, más napon.
 */
@Component({
  selector: 'app-ikt-irat',
  templateUrl: './irat.component.html',
  styleUrls: ['../iktatgato-layout/iktatgato-layout.component.scss'],
})
export class IktIratComponent implements OnInit {
  id = '';
  irat: any = null;
  csatolmanyok: any[] = [];
  kapcsolatok: any[] = [];
  posta: any[] = [];
  esemenyek: any[] = [];
  ugyintezok: any[] = [];
  hiba = '';

  szerkesztes: any = {};
  feltoltes: any = { szerep: 'melleklet', hiteles_masolat: false, eredeti_adathordozo: 'papir' };
  feltoltesFolyamatban = false;

  readonly ALLAPOT_SZINEK: Record<string, string> = {
    piszkozat: 'medium', iktatott: 'primary', kiadmanyozott: 'tertiary',
    expedialt: 'secondary', elintezett: 'success',
  };

  constructor(private ikt: IktatgatoService, private data: DataService,
              private route: ActivatedRoute, private router: Router) {}

  async ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id') || '';
    await this.betolt();
    try { this.ugyintezok = await this.ikt.ugyintezok(); } catch { this.ugyintezok = []; }
  }

  async betolt() {
    try {
      const r = await this.ikt.irat(this.id);
      this.irat = r.irat;
      this.csatolmanyok = r.csatolmanyok || [];
      this.kapcsolatok = r.kapcsolatok || [];
      this.posta = r.posta || [];
      this.esemenyek = r.esemenyek || [];
      this.szerkesztes = {
        targy: this.irat.targy || '',
        ugyintezo: this.irat.ugyintezo || '',
        hatarido: this.irat.hatarido || '',
        elintezes_modja: this.irat.elintezes_modja || '',
        hivatkozasi_szam: this.irat.hivatkozasi_szam || '',
        szoveg: this.irat.szoveg || '',
      };
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Az irat nem tölthető be.';
    }
  }

  allapotSzin(a: string) { return this.ALLAPOT_SZINEK[a] || 'medium'; }

  async ment(adat: any, uzenet: string) {
    try {
      await this.ikt.iratModosit(this.id, adat);
      this.data.sendToast(uzenet);
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    }
  }

  adatMentes() { this.ment({ ...this.szerkesztes }, 'Mentve.'); }
  kiadmanyoz() { this.ment({ kiadmanyozas: true }, 'Kiadmányozva – az irat tartalma innentől hiteles.'); }
  expedial() { this.ment({ expedialas: true }, 'Expediálva.'); }
  elintez() { this.ment({ elintezes: true, elintezes_modja: this.szerkesztes.elintezes_modja }, 'Elintézve.'); }

  /* ── csatolmányok ── */

  fajlValasztva(esemeny: any) {
    const fajl: File = esemeny?.target?.files?.[0];
    if (fajl) this.feltolt(fajl, esemeny.target);
  }

  async feltolt(fajl: File, input?: any) {
    this.feltoltesFolyamatban = true;
    try {
      await this.ikt.csatolmanyFeltolt(fajl, {
        irat: this.id,
        szerep: this.feltoltes.szerep,
        eredeti_adathordozo: this.feltoltes.eredeti_adathordozo,
        hiteles_masolat: this.feltoltes.hiteles_masolat ? 1 : '',
      });
      this.data.sendToast('Csatolva.');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A feltöltés nem sikerült.', 'error');
    } finally {
      this.feltoltesFolyamatban = false;
      if (input) input.value = '';
    }
  }

  /*  A csatolmány a TOKENES megnyitóval nyílik: a `window.open` nem vinné a belépési
   *  jegyet, és a kiszolgáló – helyesen – nem ad ki fájlt kilét nélkül. */
  megnyit(cs: any) { this.data.megnyitFajl(this.ikt.csatolmanyUrl(cs.id), cs?.nev || 'Csatolmány'); }

  async csatolmanyTorol(cs: any) {
    if (!window.confirm(`Biztosan leveszed: ${cs.nev}?`)) return;
    try {
      await this.ikt.csatolmanyTorol(cs.id);
      this.data.sendToast('Levéve.');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }

  /* ── navigáció ── */

  ugyiratra() { if (this.irat) this.router.navigateByUrl('/iktatgato/ugyirat/' + this.irat.ugyirat); }
  masikIratra(k: any) { this.router.navigateByUrl('/iktatgato/irat/' + k.masik_id); }
  postara() { this.router.navigate(['/iktatgato/postakonyv'], { queryParams: { irat: this.id } }); }

  async ervenytelenit() {
    const indok = window.prompt('Az érvénytelenítés indoka (kötelező, naplózódik):') || '';
    if (!indok.trim()) return;
    try {
      await this.ikt.ervenytelenit('irat', this.id, indok.trim());
      this.data.sendToast('Érvénytelenítve. Az iktatószám megmarad, nem adható ki újra.');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az érvénytelenítés nem sikerült.', 'error');
    }
  }
}
